import * as React from 'react';
import { Box } from '@mui/material';
import { styled } from '@mui/system';
import { useCandidateDashboardContext } from '@common/context/candidateDashboardContext';
import { JobApplied } from '@api/models/jobApplicationApi.models';
import NoDataMessage from '@components/NoDataMessage';
import { useTableSortContext } from '@common/context/tableSortContext';
import { useResponsiveLayoutContext } from '@components/Layout/ResponsiveLayout';
import DashboardLoading from '@components/TableComponents/DashboardLoading';
import SectionHeader from '@components/SectionHeader';
import TableWrapperWithSort from '@components/TableWrapperWithSort';
import { sortTableData } from '@common/helpers/sortByCategory';
import EcDataGrid from '@components/DataGrid/EcDataGrid';
import EcGridTableToolbar from '@components/DataGrid/EcGridTableToolbar';
import { GridColDef } from '@mui/x-data-grid-pro';
import { allowedGridFilterOperators } from '@common/fetches/useFetchGridTableByType';

const Styled = {
  Container: styled(Box, {
    shouldForwardProp: (prop) => prop !== 'customBreakpointReached'
  })<{ customBreakpointReached: boolean }>(({ customBreakpointReached }) => ({
    backgroundColor: 'transparent',
    flex: !customBreakpointReached ? '2 1 878px' : '1',
    marginBottom: !customBreakpointReached ? 0 : '2em',
    height: 'max-content',
    padding: '0 2em 2em 6em',
    ['@media (max-width:1100px)']: {
      padding: '0 3em 2em'
    }
  })),
  TableContainer: styled(Box, {
    shouldForwardProp: (prop) => prop !== 'isEmpty'
  })<{ isEmpty: boolean }>(({ isEmpty, theme }) => ({
    width: '100%',
    marginTop: '18px',
    backgroundColor: theme.palette.WHITE.main,
    border: `1px solid ${theme.palette.GRAY_F4.main}`,
    boxShadow: '0px 4px 4px 0px rgba(0, 0, 0, 0.25)',
    borderRadius: '4px',
    overflow: 'hidden',
    ...(isEmpty && {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '390px'
    })
  }))
};

interface Props {
  jobApplications: JobApplied[];
}

interface SortButtonProps {
  label: string;
  id: string;
}

const sortButtons: SortButtonProps[] = [
  {
    label: 'Date Applied',
    id: 'dateApplied'
  },
  {
    label: 'Status',
    id: 'status.value'
  },
  {
    label: 'Job Title',
    id: 'jobTitle'
  },
  {
    label: 'Company Name',
    id: 'companyName'
  }
];

export const CandidateTable = (props: Props): JSX.Element => {
  const { jobApplications } = props;

  const { page, rowsPerPage, sortBy, sortOrder } = useTableSortContext();

  const sortedJobApplications: JobApplied[] = React.useMemo(
    () =>
      sortTableData(
        jobApplications,
        sortOrder,
        sortBy as keyof JobApplied
      ),
    [jobApplications, sortOrder, sortBy]
  );

  const candidateTableColumns: GridColDef[] = [
    {
      field: 'dateAppliedString',
      headerName: 'Date Applied',
      flex: 1,
      filterOperators: allowedGridFilterOperators
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 1,
      valueGetter: (params): string => params.row.status.label,
      filterOperators: allowedGridFilterOperators
    },
    {
      field: 'jobTitle',
      headerName: 'Job Title',
      flex: 1,
      filterOperators: allowedGridFilterOperators
    },
    {
      field: 'companyName',
      headerName: 'Company Name',
      flex: 1,
      filterOperators: allowedGridFilterOperators
    }
  ];

  const tableRows = React.useMemo(() => {
    return sortedJobApplications
      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
      .map((jobApplication) => ({
        ...jobApplication,
        id: jobApplication.id
      }));
  }, [sortedJobApplications, page, rowsPerPage]);

  return (
    <Styled.TableContainer isEmpty={!jobApplications.length}>
      {jobApplications.length > 0 ? (
        <EcDataGrid
          autoHeight={true}
          columns={candidateTableColumns}
          rows={tableRows}
          hideFooter={true}
          slots={{
            toolbar: EcGridTableToolbar
          }}
          data-testid="job-application-table"
        />
      ) : (
        <Box p="8px">
          <NoDataMessage
            title="You haven't applied to any jobs yet."
            subtitle="Apply today on EConnect!"
          />
        </Box>
      )}
    </Styled.TableContainer>
  );
};

const ApplicationsSection: React.FC = () => {
  const { jobApplications, loaded } = useCandidateDashboardContext();

  const { customBreakpointReached } = useResponsiveLayoutContext();

  if (!loaded) {
    return (
      <Styled.Container customBreakpointReached={customBreakpointReached}>
        <DashboardLoading />
      </Styled.Container>
    );
  }

  const sectionHeaderProps = {
    sectionTitle: 'Your Applications'
  };

  const tableSortWrapperProps = {
    sortButtons,
    totalCount: jobApplications?.length,
    disabled: !jobApplications || jobApplications.length === 0,
    initialSortCategory: 'dateApplied',
    initialRowsPerPage: 5,
    displayMobile: customBreakpointReached,
    showCrown: false
  };

  return (
    <Styled.Container customBreakpointReached={customBreakpointReached}>
      <SectionHeader {...sectionHeaderProps} />
      <TableWrapperWithSort {...tableSortWrapperProps}>
        {jobApplications && <CandidateTable jobApplications={jobApplications} />}
      </TableWrapperWithSort>
    </Styled.Container>
  );
};

export default ApplicationsSection;
